/**
 * Tiny typed fetch wrapper. Handles auth tokens (access/refresh), JSON, error envelope.
 * One-shot refresh on 401 to keep sessions alive.
 */

const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:4000').replace(/\/$/, '');

const ACCESS_KEY = 'rdx.access';
const REFRESH_KEY = 'rdx.refresh';

export const tokens = {
  get access() { return localStorage.getItem(ACCESS_KEY); },
  get refresh() { return localStorage.getItem(REFRESH_KEY); },
  set(access: string | null, refresh: string | null) {
    if (access) localStorage.setItem(ACCESS_KEY, access);  else localStorage.removeItem(ACCESS_KEY);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh); else localStorage.removeItem(REFRESH_KEY);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

export interface ApiError extends Error {
  status: number;
  code: string;
  details?: any;
}

async function refresh(): Promise<boolean> {
  const refreshToken = tokens.refresh;
  if (!refreshToken) return false;
  const r = await fetch(new URL(`${BASE_URL}/api/auth/refresh`, window.location.origin).toString(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });
  if (!r.ok) { tokens.clear(); return false; }
  const data = await r.json();
  tokens.set(data.accessToken, data.refreshToken);
  return true;
}

async function rawRequest(input: RequestInfo, init: RequestInit, retry = true): Promise<Response> {
  const access = tokens.access;
  const headers = new Headers(init.headers);
  if (access) headers.set('Authorization', `Bearer ${access}`);
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  const resp = await fetch(input, { ...init, headers });
  if (resp.status === 401 && retry) {
    if (await refresh()) return rawRequest(input, init, false);
  }
  return resp;
}

async function handle<T>(resp: Response): Promise<T> {
  const ct = resp.headers.get('content-type') ?? '';
  if (resp.ok) {
    if (ct.includes('application/json')) return (await resp.json()) as T;
    return (await resp.blob()) as unknown as T;
  }
  let payload: any = null;
  try { payload = ct.includes('application/json') ? await resp.json() : await resp.text(); } catch {}
  const err: ApiError = Object.assign(new Error(payload?.error?.message || resp.statusText), {
    status: resp.status,
    code: payload?.error?.code || 'HTTP_ERROR',
    details: payload?.error?.details,
  });
  throw err;
}

export const api = {
  async get<T>(path: string, params?: Record<string, any>): Promise<T> {
    const url = new URL(BASE_URL + path, window.location.origin);
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        if (v != null && v !== '') url.searchParams.set(k, String(v));
      }
    }
    return handle<T>(await rawRequest(url.toString(), { method: 'GET' }));
  },
  async post<T>(path: string, body?: any): Promise<T> {
    return handle<T>(await rawRequest(BASE_URL + path, {
      method: 'POST', body: body !== undefined ? JSON.stringify(body) : undefined,
    }));
  },
  async put<T>(path: string, body?: any): Promise<T> {
    return handle<T>(await rawRequest(BASE_URL + path, {
      method: 'PUT', body: body !== undefined ? JSON.stringify(body) : undefined,
    }));
  },
  async patch<T>(path: string, body?: any): Promise<T> {
    return handle<T>(await rawRequest(BASE_URL + path, {
      method: 'PATCH', body: body !== undefined ? JSON.stringify(body) : undefined,
    }));
  },
  async delete<T>(path: string, body?: any): Promise<T> {
    return handle<T>(await rawRequest(BASE_URL + path, {
      method: 'DELETE', body: body !== undefined ? JSON.stringify(body) : undefined,
    }));
  },
  async getBlob(path: string): Promise<Blob> {
    const resp = await rawRequest(BASE_URL + path, { method: 'GET' });
    if (!resp.ok) await handle(resp);
    return resp.blob();
  },
};

export { BASE_URL };
